import classNames from "classnames";
import type { Immutable } from "immer";
import React, { useContext } from "react";
import { useRovingTabList } from "tsi-common-react/src/hooks/useRovingTabList";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { jt, t } from "ttag";

import { Context } from "@reactivated";
import type { templates } from "@reactivated";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

/**
 * Faithful port of the legacy server-rendered ``cms/blocks/flagship/products.html``
 * (FlagshipPage ``ProductsBlock``, a StructBlock labeled "DEPRECATED: Available
 * Products" but still rendered via the flagship ``{% include_block %}`` loop)
 * (#33473).
 *
 * The block ``value`` carried the product lists, but the per-product
 * ``{% if purchase_info.price.exists %}`` gate, ``product.get_title``, and the
 * page-level ``page.title`` / ``page.show_franchisee_disclaimer`` are
 * request/page-derived, so they are all resolved server-side by the
 * ``products_block_data`` templatetag and threaded in as side-channel props
 * (BreadcrumbsBlock / FlagshipProductAssortment precedent) -- the per-tab group
 * lists arrive already price-filtered, matching the legacy gate.
 *
 * Tab switching (roving tabindex, click, and keyboard) is owned here via the
 * shared ``useRovingTabList`` hook and component state; the rendered markup (the
 * vestigial ``data-tabs`` attrs, ``role``/``aria-*``, the ``--is-active``
 * modifiers, the tab/pane ids) is reproduced verbatim so the default +
 * post-click states stay byte-identical to the legacy.
 *
 * Per-group: ``style == "breeze"`` -> the static ``icon-breeze.png`` in
 * ``.prodtabs__category--breeze`` (alt "Tempur-Breeze"); otherwise an
 * ``<h3 class="prodtabs__category">{title}</h3>`` heading.
 *
 * The tab-nav images are static assets rebuilt from ``STATIC_URL`` (eager
 * ``<img>``, the puppeteer/screenshot render -- ProductAssortment precedent).
 * NOTE: the legacy ``.prodtabs__offer`` images
 * (``{% if page.offer_matt_default %}`` etc.) reference FlagshipPage fields
 * REMOVED in migration 0333 -- always-falsy today -- so the offer div is always
 * empty; reproduced as an empty ``<div class="prodtabs__offer">``.
 *
 * The mattresses-pane border note alone is gated on
 * ``show_franchisee_disclaimer`` (stores vs. owned stores); the bases + pillows
 * panes hardcode the "owned stores" wording (no conditional), preserved.
 */

type Group = templates.FlagshipProductsBlockTemplate["mattresses"][number];

const TABS = [
    {
        key: "mattresses",
        paneId: "mattresses-pane",
        tabId: "mattresses-tab",
        img: "tabnav-mattresses.jpg",
    },
    {
        key: "bases",
        paneId: "bases-pane",
        tabId: "bases-tab",
        img: "tabnav-bases.jpg",
    },
    {
        key: "pillows",
        paneId: "pillows-pane",
        tabId: "pillows-tab",
        img: "tabnav-pillows.jpg",
    },
] as const;

const ProductGroup = (
    props: Immutable<{ group: Group; staticURL: string }>,
): React.JSX.Element => {
    const group = props.group;
    return (
        <div className="prodtabs__prodlist">
            {group.style === "breeze" ? (
                <div className="prodtabs__category prodtabs__category--breeze">
                    <img
                        src={`${props.staticURL}img/flagship-page/icon-breeze.png`}
                        alt="Tempur-Breeze"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            ) : (
                <h3 className="prodtabs__category">{group.title}</h3>
            )}
            <ul>
                {group.products.map((title, i) => (
                    <li key={i}>{title}</li>
                ))}
            </ul>
        </div>
    );
};

const Pane = (
    props: Immutable<{
        paneId: string;
        tabId: string;
        active: boolean;
        pageTitle: string;
        groups: readonly Group[];
        // The mattresses pane gates its border note on the disclaimer flag; the
        // bases/pillows panes pass ``null`` (always the "owned stores" wording).
        showFranchiseeDisclaimer: boolean | null;
        staticURL: string;
    }>,
): React.JSX.Element => {
    const pageTitle = props.pageTitle;
    // Each <sup>® must be a DISTINCT keyed element (reusing one element across
    // multiple jt interpolations triggers React duplicate-key warnings) -- the
    // FinancingModalTriggerHomepage sup1/sup2 precedent.
    const noteReg = <sup key="reg-note">&reg;</sup>;
    const bReg1 = <sup key="reg-b1">&reg;</sup>;
    const bReg2 = <sup key="reg-b2">&reg;</sup>;
    const bReg3 = <sup key="reg-b3">&reg;</sup>;
    // Legacy mattresses pane: ``{% if page.show_franchisee_disclaimer %}`` ->
    // "stores", else -> "owned stores". Bases/pillows always "owned stores".
    const borderNote =
        props.showFranchiseeDisclaimer === true
            ? jt`Tempur-Pedic${bReg1} stores are your best source to experience Tempur-Pedic${bReg2} products. All Tempur-Pedic${bReg3} products are available for purchase at the ${pageTitle}`
            : jt`Tempur-Pedic${bReg1} owned stores are your best source to experience Tempur-Pedic${bReg2} products. All Tempur-Pedic${bReg3} products are available for purchase at the ${pageTitle}`;
    return (
        <div
            className={classNames("prodtabs__pane", {
                "prodtabs__pane--is-active": props.active,
            })}
            id={props.paneId}
            role="tabpanel"
            aria-labelledby={props.tabId}
        >
            <div className="prodtabs__pane-wrapper l-capped-width">
                <div className="prodtabs__products">
                    <p className="prodtabs__note">
                        {jt`Experience these Tempur-Pedic${noteReg} products at the ${pageTitle}`}
                    </p>
                    <div className="prodtabs__prodgroup">
                        {props.groups.map((group, i) => (
                            <ProductGroup
                                key={i}
                                group={group}
                                staticURL={props.staticURL}
                            />
                        ))}
                    </div>
                    <p className="prodtabs__note prodtabs__note--border">
                        {borderNote}
                    </p>
                </div>
                <div className="prodtabs__offer"></div>
            </div>
        </div>
    );
};

export const Template = (
    props: Immutable<templates.FlagshipProductsBlockTemplate>,
): React.JSX.Element => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const { activeIndex, getTabProps } = useRovingTabList({
        count: TABS.length,
    });
    const groups = [props.mattresses, props.bed_bases, props.pillows];
    // Only the mattresses pane gates on the disclaimer flag; bases/pillows pass
    // null (always "owned stores").
    const paneDisclaimer: (boolean | null)[] = [
        props.show_franchisee_disclaimer,
        null,
        null,
    ];

    // jt preserves the inline <span>s of the legacy ``{% blocktrans %}`` labels.
    const adjustableSpan = <span key="adj">Adjustable </span>;
    const pillowsSpan = <span key="pil"> &amp; more</span>;
    const tabLabels = [
        <>Mattresses</>,
        <>{jt`${adjustableSpan}Bases`}</>,
        <>{jt`Pillows${pillowsSpan}`}</>,
    ];
    // Tab titles preserve the legacy "Adjustble" typo verbatim.
    const tabTitles = [t`Mattresses`, t`Adjustble Bases`, t`Pillows & More`];

    return (
        <div className="products">
            <div className="lead-in">
                <h1 className="lead-in__headline lead-in__headline--thin lead-in__headline--large">
                    {t`Product Availability`}
                </h1>
                <div className="lead-in__subtext">
                    <p>
                        {richT(
                            t`Tempur-Pedic<sup>®</sup> Stores offer the widest assortment of genuine Tempur-Pedic products.`,
                        )}
                    </p>
                </div>
            </div>

            <div className="prodtabs l-capped-width">
                <span className="ada-screenreader-only">{t`Use arrow keys to navigate tabs`}</span>

                <div
                    className="prodtabs__tabs"
                    role="tablist"
                    aria-label={t`Product Availability`}
                >
                    {TABS.map((tab, i) => (
                        <a
                            href={`#${tab.paneId}`}
                            data-tabs="prodtabs"
                            className={classNames("prodtabs__tab", {
                                "prodtabs__tab--is-active": activeIndex === i,
                            })}
                            title={tabTitles[i]}
                            role="tab"
                            id={tab.tabId}
                            aria-controls={tab.paneId}
                            key={tab.key}
                            {...getTabProps(i)}
                        >
                            <div className="prodtabs__image-wrapper">
                                <img
                                    className="prodtabs__image"
                                    src={`${staticURL}img/flagship-page/${tab.img}`}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="prodtabs__label type_x1point125">
                                {tabLabels[i]}
                            </div>
                        </a>
                    ))}
                </div>

                {TABS.map((tab, i) => (
                    <Pane
                        key={tab.key}
                        paneId={tab.paneId}
                        tabId={tab.tabId}
                        active={activeIndex === i}
                        pageTitle={props.page_title}
                        groups={groups[i]}
                        showFranchiseeDisclaimer={paneDisclaimer[i]}
                        staticURL={staticURL}
                    />
                ))}
            </div>
        </div>
    );
};
